import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function PortalLoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || "/portal/dashboard";

    const [form, setForm] = useState({ email: "", password: "" });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const update = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors((prev) => ({ ...prev, [k]: null }));
    };

    const submit = async (e) => {
        e.preventDefault();
        const next = {};
        if (!EMAIL_RE.test(form.email)) next.email = "Enter a valid email.";
        if (!form.password) next.password = "Password is required.";
        setErrors(next);
        if (Object.keys(next).length) return;

        setSubmitting(true);
        try {
            await login(form);
            toast.success("Welcome back.");
            navigate(from, { replace: true });
        } catch (err) {
            const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            className="grid min-h-[calc(100vh-72px)] bg-brand-charcoal md:grid-cols-2"
            data-testid="portal-login-page"
        >
            <div className="relative hidden md:block">
                <img
                    src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1400&q=80"
                    alt=""
                    className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-brand-charcoal/70 via-brand-charcoal/40 to-brand-charcoal/80" />
                <div className="absolute inset-0 flex flex-col justify-end p-12 text-brand-offwhite">
                    <p className="font-mono-tag text-brand-gold">
                        [ TRAINING PORTAL ]
                    </p>
                    <h1 className="mt-4 font-display text-6xl leading-[1.05]">
                        WELCOME <br />
                        <span className="text-brand-gold">BACK.</span>
                    </h1>
                </div>
            </div>

            <div className="grid place-items-center bg-brand-offwhite p-8 md:p-16">
                <Reveal className="w-full max-w-md">
                    <p className="font-mono-tag text-brand-gold">[ LOG IN ]</p>
                    <h2 className="mt-4 font-display text-5xl leading-[1.1]">
                        SIGN IN TO CONTINUE
                    </h2>
                    <form onSubmit={submit} noValidate className="mt-10" data-testid="login-form">
                        <div>
                            <label className="form-label" htmlFor="login-email">
                                Email
                            </label>
                            <input
                                id="login-email"
                                type="email"
                                value={form.email}
                                onChange={update("email")}
                                autoComplete="email"
                                className="form-input"
                                data-testid="login-email"
                            />
                            {errors.email && (
                                <p
                                    className="mt-1 font-mono-tag text-destructive"
                                    data-testid="login-email-error"
                                >
                                    {errors.email}
                                </p>
                            )}
                        </div>
                        <div className="mt-6">
                            <label className="form-label" htmlFor="login-password">
                                Password
                            </label>
                            <input
                                id="login-password"
                                type="password"
                                value={form.password}
                                onChange={update("password")}
                                autoComplete="current-password"
                                className="form-input"
                                data-testid="login-password"
                            />
                            {errors.password && (
                                <p
                                    className="mt-1 font-mono-tag text-destructive"
                                    data-testid="login-password-error"
                                >
                                    {errors.password}
                                </p>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-primary mt-10 w-full disabled:cursor-not-allowed disabled:opacity-60"
                            data-testid="login-submit"
                        >
                            {submitting ? "Signing in…" : "Sign in"}
                            <ArrowUpRight className="h-4 w-4" />
                        </button>
                        <p className="mt-6 font-body text-sm text-brand-steel">
                            New here?{" "}
                            <Link
                                to="/portal/signup"
                                className="text-brand-gold underline-offset-4 hover:underline"
                                data-testid="login-signup-link"
                            >
                                Create an account
                            </Link>
                        </p>
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
